import { FormEvent, useEffect, useRef, useState } from 'react';
import {
  deleteEmployeePhoto,
  fetchEmployeeCard,
  fetchEmployeePhotoUrl,
  suggestEmployeeNo,
  updateEmployeeCard,
  uploadEmployeePhoto,
} from '../../api/org';
import { COMPANY_ROLE_LABEL } from '../../types/companyRole';
import { Department, EmployeeCardPayload } from '../../types/org';
import './OrgAdminPanel.css';

import { reportError } from '../../utils/notify';
interface EmployeeFormProps {
  userId: number;
  departments: Department[];
  onCancel: () => void;
  onSaved: () => void;
}

const EMPTY: EmployeeCardPayload = {
  displayName: '',
  employeeNo: '',
  jobTitle: '',
  email: '',
  phone: '',
  birthDate: '',
  address: '',
  hireDate: '',
  memo: '',
  role: 'MEMBER',
  departmentId: null,
};

function EmployeeForm({ userId, departments, onCancel, onSaved }: EmployeeFormProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState<EmployeeCardPayload>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [error, setError] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [fallbackUrl, setFallbackUrl] = useState<string | null>(null);
  const [hasPhoto, setHasPhoto] = useState(false);
  const [employeeNoHint, setEmployeeNoHint] = useState('');

  useEffect(() => {
    let objectUrl: string | null = null;
    const load = async () => {
      try {
        setLoading(true);
        const card = await fetchEmployeeCard(userId);
        let employeeNo = card.employeeNo || '';
        let hint = '';
        if (!employeeNo.trim()) {
          try {
            const suggestion = await suggestEmployeeNo();
            employeeNo = suggestion.suggestedValue?.trim() || '';
            const last = suggestion.lastValue?.trim() || '';
            hint = last
              ? `직전 사번: ${last} → 끝자리 증가값을 제안했습니다.`
              : '이 회사의 첫 사번입니다. 규칙을 정해 입력하세요. (예: E-2026-001)';
          } catch {
            hint = '최근 사번을 불러오지 못했습니다. 직접 입력하세요.';
          }
        }
        setForm({
          displayName: card.displayName || '',
          employeeNo,
          jobTitle: card.jobTitle || '',
          email: card.email || '',
          phone: card.phone || '',
          birthDate: card.birthDate || '',
          address: card.address || '',
          hireDate: card.hireDate || '',
          memo: card.memo || '',
          role: card.role || 'MEMBER',
          departmentId: card.departmentId ?? null,
        });
        setEmployeeNoHint(hint);
        setHasPhoto(Boolean(card.hasPhoto));
        setFallbackUrl(card.profileImageUrl || null);
        if (card.hasPhoto) {
          objectUrl = await fetchEmployeePhotoUrl(userId);
          setPreviewUrl(objectUrl);
        } else {
          setPreviewUrl(null);
        }
        setError('');
      } catch {
        reportError(setError, '인사카드를 불러오지 못했습니다.');
      } finally {
        setLoading(false);
      }
    };
    void load();
    return () => {
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [userId]);

  const refreshPhoto = async (nextHasPhoto: boolean) => {
    if (previewUrl && previewUrl.startsWith('blob:')) {
      window.URL.revokeObjectURL(previewUrl);
    }
    if (nextHasPhoto) {
      const url = await fetchEmployeePhotoUrl(userId);
      setPreviewUrl(url);
      setHasPhoto(true);
    } else {
      setPreviewUrl(null);
      setHasPhoto(false);
    }
  };

  const handlePickPhoto = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      setPhotoBusy(true);
      setError('');
      await uploadEmployeePhoto(userId, file);
      await refreshPhoto(true);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '사진 등록에 실패했습니다.';
      reportError(setError, message);
    } finally {
      setPhotoBusy(false);
    }
  };

  const handleRemovePhoto = async () => {
    if (!hasPhoto) {
      setPreviewUrl(null);
      return;
    }
    const ok = window.confirm('등록된 사진을 삭제할까요?');
    if (!ok) return;
    try {
      setPhotoBusy(true);
      setError('');
      await deleteEmployeePhoto(userId);
      await refreshPhoto(false);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '사진 삭제에 실패했습니다.';
      reportError(setError, message);
    } finally {
      setPhotoBusy(false);
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    try {
      setSaving(true);
      setError('');
      await updateEmployeeCard(userId, {
        displayName: form.displayName?.trim() || null,
        employeeNo: form.employeeNo?.trim() || null,
        jobTitle: form.jobTitle?.trim() || null,
        email: form.email?.trim() || null,
        phone: form.phone?.trim() || null,
        birthDate: form.birthDate || null,
        address: form.address?.trim() || null,
        hireDate: form.hireDate || null,
        memo: form.memo?.trim() || null,
        role: form.role || 'MEMBER',
        departmentId: form.departmentId ?? null,
      });
      onSaved();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '저장에 실패했습니다.';
      reportError(setError, message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="contract-panel__hint">불러오는 중...</p>;
  }

  const shownPhoto = previewUrl || fallbackUrl;

  return (
    <form className="org-form" onSubmit={handleSubmit}>
      <header className="org-form__header">
        <div>
          <p className="org-admin__eyebrow">인사카드 수정</p>
          <h2>인원 정보 편집</h2>
        </div>
        <div className="org-form__actions">
          <button type="button" className="is-ghost" onClick={onCancel}>
            취소
          </button>
          <button type="submit" disabled={saving || photoBusy}>
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </header>

      {error && <p className="contract-panel__error">{error}</p>}

      <div className="org-form__layout">
        <section className="org-form__section org-form__section--photo">
          <div className="org-form__section-head">
            <h3>사진</h3>
            <span>jpg / png / webp / gif</span>
          </div>
          <div className="org-form__photo-row">
            <div className="org-form__photo">
              {shownPhoto ? (
                <img src={shownPhoto} alt="" />
              ) : (
                <span>{(form.displayName || '?').slice(0, 1)}</span>
              )}
            </div>
            <div className="org-form__photo-actions">
              <button
                type="button"
                disabled={photoBusy}
                onClick={() => fileInputRef.current?.click()}
              >
                {photoBusy ? '처리 중...' : hasPhoto ? '사진 변경' : '사진 등록'}
              </button>
              {(hasPhoto || previewUrl) && (
                <button
                  type="button"
                  className="is-ghost is-danger-text"
                  disabled={photoBusy}
                  onClick={() => void handleRemovePhoto()}
                >
                  사진 삭제
                </button>
              )}
              <p>상세·목록에 바로 반영됩니다.</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                hidden
                onChange={(e) => void handlePickPhoto(e)}
              />
            </div>
          </div>
        </section>

        <section className="org-form__section">
          <div className="org-form__section-head">
            <h3>기본 정보</h3>
            <span>이름 · 사번 · 담당</span>
          </div>
          <div className="org-form__grid">
            <label>
              이름
              <input
                value={form.displayName || ''}
                onChange={(e) => setForm((prev) => ({ ...prev, displayName: e.target.value }))}
                maxLength={100}
                placeholder="이름 입력"
                required
              />
            </label>
            <label>
              사번
              <input
                value={form.employeeNo || ''}
                onChange={(e) => setForm((prev) => ({ ...prev, employeeNo: e.target.value }))}
                maxLength={50}
                placeholder="E-2026-001"
              />
              {employeeNoHint ? <small className="org-form__hint">{employeeNoHint}</small> : null}
            </label>
            <label>
              담당
              <input
                value={form.jobTitle || ''}
                onChange={(e) => setForm((prev) => ({ ...prev, jobTitle: e.target.value }))}
                maxLength={100}
              />
            </label>
          </div>
        </section>

        <section className="org-form__section">
          <div className="org-form__section-head">
            <h3>조직</h3>
            <span>역할 · 부서 배치</span>
          </div>
          <div className="org-form__grid">
            <label>
              사내 역할
              <select
                value={form.role || 'MEMBER'}
                onChange={(e) => setForm((prev) => ({ ...prev, role: e.target.value }))}
              >
                {Object.entries(COMPANY_ROLE_LABEL).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              부서
              <select
                value={form.departmentId ?? ''}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    departmentId: e.target.value ? Number(e.target.value) : null,
                  }))
                }
              >
                <option value="">미배치</option>
                {departments.map((dept) => (
                  <option key={dept.departmentId} value={dept.departmentId}>
                    {dept.departmentName}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </section>

        <section className="org-form__section">
          <div className="org-form__section-head">
            <h3>연락처</h3>
            <span>이메일 · 전화 · 주소</span>
          </div>
          <div className="org-form__grid">
            <label>
              이메일
              <input
                type="email"
                value={form.email || ''}
                onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                maxLength={255}
              />
            </label>
            <label>
              연락처
              <input
                value={form.phone || ''}
                onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
                maxLength={50}
              />
            </label>
            <label className="is-full">
              주소
              <input
                value={form.address || ''}
                onChange={(e) => setForm((prev) => ({ ...prev, address: e.target.value }))}
                maxLength={500}
              />
            </label>
          </div>
        </section>

        <section className="org-form__section">
          <div className="org-form__section-head">
            <h3>인사 일정</h3>
            <span>생년월일 · 입사일</span>
          </div>
          <div className="org-form__grid">
            <label>
              생년월일
              <input
                type="date"
                value={form.birthDate || ''}
                onChange={(e) => setForm((prev) => ({ ...prev, birthDate: e.target.value }))}
              />
            </label>
            <label>
              입사일
              <input
                type="date"
                value={form.hireDate || ''}
                onChange={(e) => setForm((prev) => ({ ...prev, hireDate: e.target.value }))}
              />
              <small className="org-form__hint">
                입사일을 저장하면 올해는 1개월 만근마다 1일(최대 11일)로 누적되고, 다음 해부터는 15일 기준으로 한 번에 부여됩니다.
              </small>
            </label>
          </div>
        </section>

        <section className="org-form__section">
          <div className="org-form__section-head">
            <h3>메모</h3>
            <span>비고</span>
          </div>
          <label className="org-form__memo">
            <textarea
              value={form.memo || ''}
              onChange={(e) => setForm((prev) => ({ ...prev, memo: e.target.value }))}
              rows={4}
              maxLength={2000}
              placeholder="특이사항이나 참고 내용을 남겨 주세요."
            />
          </label>
        </section>
      </div>
    </form>
  );
}

export default EmployeeForm;
